<script lang="ts">
import type { UploadScope as UploadScopeType } from '#layers/assets/stores/upload';

export interface IconImagePickerProps {
	/** Upload scope for the image tab. */
	scope: UploadScopeType;
	pickColor?: boolean;
	pickEmoji?: boolean;
	placeholderIcon?: string;
	defaultColor?: string;
	block?: boolean;
	size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
	variant?: 'solid' | 'soft' | 'ghost' | 'link';
	spacing?: 'none' | 'tight' | 'comfortable';
	colorPresets?: string[];
	clear?: boolean;
	readonly?: boolean;
	disabled?: boolean;
}
</script>

<script lang="ts" setup>
import type { TrackedRequest } from '#layers/base/public';
import type { GlobalFileId } from '#layers/data-model/public';
import { computed } from 'vue';
import { useAppConfig } from '#app/config';
import RenderAssetImage from '#layers/assets/components/RenderAssetImage.vue';
import { UploadScope } from '#layers/assets/stores/upload';
import { createRequestTracker, isNil } from '#layers/base/public';
import { IconPicker } from '#layers/ui/public';
import IconImagePickerTabImage from './IconImagePickerTabImage.vue';

// Preserve the base component defaults when forwarding absent props.
const {
	clear = true,
	variant = 'link',
	spacing = 'comfortable',
} = defineProps<IconImagePickerProps>();

const icon = defineModel<string | null>();
const color = defineModel<string | null>('color');
const image = defineModel<GlobalFileId | null>('image');

const appConfig = useAppConfig();
const imageUploads = createRequestTracker();
let activeImageUpload: TrackedRequest | null = null;

const hasIcon = computed(() => !isNil(icon.value));
const hasImage = computed(() => !isNil(image.value));
const hasValue = computed(() => hasIcon.value || hasImage.value);

const extraTab = computed(() => ({
	value: 'image',
	label: 'icon_picker.tab_image',
	active: hasImage.value,
}));

function startImageUpload() {
	activeImageUpload = imageUploads.start();
}

// Uploads are serialized by the image tab, so the latest tracked request is the in-flight one.
function selectImage(fileId: GlobalFileId) {
	if (!activeImageUpload?.isCurrent()) return;
	imageUploads.invalidate();
	icon.value = null;
	image.value = fileId;
}

function handleIconUpdate(value: string | null | undefined) {
	icon.value = value;
	if (isNil(value)) return;
	imageUploads.invalidate();
	image.value = null;
}

function handleClear() {
	imageUploads.invalidate();
	image.value = null;
}
</script>

<template>
	<IconPicker
		v-model:color="color"
		:model-value="icon"
		:pick-color
		:pick-emoji
		:placeholder-icon
		:default-color
		:block
		:size
		:variant
		:spacing
		:color-presets
		:clear
		:readonly
		:disabled
		:extra-tab
		:has-value
		@update:model-value="handleIconUpdate"
		@clear="handleClear"
	>
		<template #preview="{ displayedIcon, effectiveColor, hasColor, variant: previewVariant, spacing: previewSpacing, mode }">
			<span
				v-if="image"
				:style="hasColor ? { backgroundColor: `color-mix(in srgb, ${effectiveColor} 10%, transparent)` } : undefined"
				:class="mode === 'block'
					? 'inline-flex size-4 shrink-0 items-center justify-center overflow-hidden rounded-xs'
					: 'group/image relative flex size-full items-center justify-center overflow-hidden rounded-[10%]'"
			>
				<RenderAssetImage
					:file="image"
					:workspace="scope === UploadScope.Workspace"
					:class="mode === 'block' ? 'size-full object-cover' : 'size-full rounded-[10%] object-cover'"
				/>
				<span
					v-if="mode !== 'block'"
					class="
						absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 transition-opacity
						group-hover/image:opacity-100
					"
				>
					<DIcon :name="appConfig.ui.icons.edit" class="size-4 text-white" />
				</span>
			</span>
			<DIconFrame
				v-else-if="displayedIcon"
				:name="displayedIcon"
				:color="effectiveColor"
				:variant="previewVariant"
				:spacing="previewSpacing"
				:disabled
				:class="mode === 'block' ? 'size-4' : 'aspect-square h-full'"
			/>
			<div v-else-if="mode === 'block'" class="size-4" />
			<DIcon v-else name="lucide:plus" class="size-5 text-dimmed" />
		</template>

		<template #extra-tab>
			<IconImagePickerTabImage
				:model-value="image"
				:scope
				:readonly
				:disabled
				@upload-start="startImageUpload"
				@select="selectImage"
			/>
		</template>
	</IconPicker>
</template>
